/* eslint-disable */
import React from 'react';
import {
  Button,
  Space,
  Table,
  Tag,
  Tooltip
} from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import type { TableProps } from 'antd/es/table';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  SyncOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

interface DynamicTableProps<T = any> extends Omit<TableProps<T>, 'columns'> {
  data: T[];
  tableType?: string;
  onViewDetails?: (record: T) => void;
  customColumns?: string[];
  excludeColumns?: string[];
  showActions?: boolean;
  useCustomColumns?: boolean;
  customColumnsConfig?: ColumnsType<T>;
}

const DynamicTable = <T extends Record<string, any>>({
  data,
  tableType = 'default',
  onViewDetails,
  customColumns,
  excludeColumns = [],
  showActions = true,
  useCustomColumns = false,
  customColumnsConfig,
  ...tableProps
}: DynamicTableProps<T>) => {
  
  // Generate dynamic columns based on data structure
  const generateDynamicColumns = (): ColumnsType<T> => {
    // If custom columns are provided and useCustomColumns is true, return them
    if (useCustomColumns && customColumnsConfig && customColumnsConfig.length > 0) {
      return customColumnsConfig;
    }

    if (!data || data.length === 0) {
      return [];
    }

    const sampleItem = data[0];
    const dynamicColumns: ColumnsType<T> = [];

    // Common columns for all tables
    dynamicColumns.push({
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      fixed: 'left',
      render: (_text: any, _record: T, idx: number) => {
        const paginationConfig = tableProps.pagination;
        const currentPage = (paginationConfig && typeof paginationConfig === 'object') ? paginationConfig.current || 1 : 1;
        const pageSize = (paginationConfig && typeof paginationConfig === 'object') ? paginationConfig.pageSize || 10 : 10;
        const globalIndex = (currentPage - 1) * pageSize + (idx + 1);
        return <span>{globalIndex}</span>;
      }
    });

    // Status column with dynamic styling
    if (sampleItem.status != null && !excludeColumns.includes('status')) {
      dynamicColumns.push({
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 150,
        fixed: 'left',
        render: (status: string) => {
          const statusConfig = {
            COMPLETED: { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed Refund' },
            APPROVED: { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed Refund' },
            PENDING: { color: 'warning', icon: <ClockCircleOutlined />, label: 'Pending Refund' },
            INITIATED: { color: 'processing', icon: <SyncOutlined />, label: 'Initiated Refund' },
            'RECEIVED REQUEST': { color: 'processing', icon: <InfoCircleOutlined />, label: 'Received Request' },
            REJECTED: { color: 'error', icon: <CloseCircleOutlined />, label: 'Rejected' },
            'Downloaded to be Refunded': { color: 'success', icon: <CheckCircleOutlined />, label: 'Completed Refund' }
          };
          const config = statusConfig[status as keyof typeof statusConfig] ?? { 
            color: 'default', 
            icon: <InfoCircleOutlined />,
            label: status
          };

          return (
            <Tag color={config.color} icon={config.icon}>
              {config.label}
            </Tag>
          );
        },
        filters: [
          { text: 'Completed Refund', value: 'COMPLETED' },
          { text: 'Pending Refund', value: 'PENDING' },
          { text: 'Initiated Refund', value: 'INITIATED' },
          { text: 'Rejected', value: 'REJECTED' }
        ],
        onFilter: (value: any, record: T) => {
          const s = record.status;
          if (value === 'COMPLETED') {
            return s === 'COMPLETED' || s === 'APPROVED' || s === 'Downloaded to be Refunded';
          }
          return s === value;
        }
      });
    }

    // Amount column with formatting
    if (sampleItem.amount != null && !excludeColumns.includes('amount')) {
      dynamicColumns.push({
        title: 'Amount',
        dataIndex: 'amount',
        key: 'amount',
        width: 120,
        render: (amount: string | number) => {
          const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
          return (
            <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
              {numAmount.toLocaleString()} RWF
            </span>
          );
        },
        sorter: (a: T, b: T) => {
          const amountA = typeof a.amount === 'string' ? parseFloat(a.amount) : a.amount;
          const amountB = typeof b.amount === 'string' ? parseFloat(b.amount) : b.amount;
          return amountA - amountB;
        }
      });
    }

    // Define specific columns for different table types
    const getTableSpecificColumns = (type: string): string[] => {
      switch (type) {
        case 'bidders':
          return ['transaction_reference', 'beneficiary_account_name', 'beneficiary_bank_name', 
                  'beneficiary_account_number', 'debit_account_name', 'bank_code', 'reason', 'created_at'];
        case 'bailiffs':
          return ['transaction_reference', 'beneficiary_account_name', 'beneficiary_bank_name', 
                  'beneficiary_account_number', 'debit_account_name', 'bank_code', 'reason', 'created_at'];
        case 'comprehensive':
          return ['table_source', 'request_type', 'transaction_reference', 'beneficiary_account_name', 
                  'beneficiary_bank_name', 'bank_code', 'reason', 'created_at', 'updated_at'];
        default:
          return [];
      }
    };

    // Get columns to display
    let columnsToDisplay: string[] = [];
    
    if (customColumns && customColumns.length > 0) {
      columnsToDisplay = customColumns;
    } else {
      columnsToDisplay = getTableSpecificColumns(tableType);
    }

    // Add specific columns if defined, otherwise add all available columns
    if (columnsToDisplay.length > 0) {
      columnsToDisplay.forEach(key => {
        if (sampleItem[key] != null && !excludeColumns.includes(key)) {
          const columnConfig: any = {
            title: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            dataIndex: key,
            key: key,
            width: 150,
            ellipsis: true
          };

          // Special handling for specific fields
          if (key.includes('date') || key.includes('created_at') || key.includes('updated_at')) {
            columnConfig.render = (date: string) => date !== '' ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-';
            columnConfig.sorter = (a: T, b: T) => dayjs(a[key]).unix() - dayjs(b[key]).unix();
          } else if (key.includes('name') || key.includes('full_name')) {
            columnConfig.width = 200;
            columnConfig.render = (text: string) => text ? text.toUpperCase() : '';
          } else if (key.includes('email')) {
            columnConfig.render = (email: string) => email !== '' ? <a href={`mailto:${email}`}>{email}</a> : '-';
          } else if (key.includes('phone')) {
            columnConfig.render = (phone: string) => phone !== '' ? <a href={`tel:${phone}`}>{phone}</a> : '-';
          } else if (key.includes('reference') || key.includes('id')) {
            columnConfig.width = 180;
            columnConfig.render = (value: string) => (
              <span style={{ fontFamily: 'inherit' }}>
                {value !== '' ? value : '-'}
              </span>
            );
          } else if (key === 'transaction_reference') {
            columnConfig.width = 150;
            columnConfig.ellipsis = true;
          }

          dynamicColumns.push(columnConfig);
        }
      });
    } else {
      // Fallback: add all available columns for other table types
      Object.keys(sampleItem).forEach(key => {
        if (key === 'index' || key === 'status' || key === 'amount' || key === 'key' || excludeColumns.includes(key)) {
          return; // Skip already handled columns
        }

        const columnConfig: any = {
          title: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          dataIndex: key,
          key: key,
          width: 150,
          ellipsis: true
        };

        // Special handling for specific fields
        if (key.includes('date') || key.includes('created_at') || key.includes('updated_at')) {
          columnConfig.render = (date: string) => date !== '' ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-';
          columnConfig.sorter = (a: T, b: T) => dayjs(a[key]).unix() - dayjs(b[key]).unix();
        } else if (key.includes('name') || key.includes('full_name')) {
          columnConfig.width = 200;
          columnConfig.render = (text: string) => text ? text.toUpperCase() : '';
        } else if (key.includes('email')) {
          columnConfig.render = (email: string) => email !== '' ? <a href={`mailto:${email}`}>{email}</a> : '-';
        } else if (key.includes('phone')) {
          columnConfig.render = (phone: string) => phone !== '' ? <a href={`tel:${phone}`}>{phone}</a> : '-';
        } else if (key.includes('reference') || key.includes('id')) {
          columnConfig.width = 180;
          columnConfig.render = (value: string) => (
            <span style={{ fontFamily: 'inherit' }}>
              {value !== '' ? value : '-'}
            </span>
          );
        }

        dynamicColumns.push(columnConfig);
      });
    }

    // Action column with enhanced view functionality
    if (showActions) {
      dynamicColumns.push({
        title: 'Actions',
        key: 'actions',
        width: 120,
        fixed: 'right',
        render: (_: any, record: T) => (
          <Space>
            <Tooltip title="View Details">
              <Button
                type="primary"
                icon={<EyeOutlined />}
                size="small"
                onClick={() => {
                  if (onViewDetails) {
                    onViewDetails(record);
                  } else {
                    console.log('View details for:', record);
                  }
                }}
              >
                View
              </Button>
            </Tooltip>
          </Space>
        )
      });
    }

    return dynamicColumns;
  };

  const columns = generateDynamicColumns();

  const resolvedRowKey =
    tableProps.rowKey ??
    ((record: T) => {
      const key =
        (record as any).key ??
        (record as any).id ??
        (record as any).bidder_id ??
        (record as any).bid_security_id ??
        (record as any).transaction_reference ??
        (record as any).refund_request_id ??
        (record as any).bailiff_refund_request_id ??
        (record as any).bid_security_payment_id ??
        (record as any).bidder?.bidder_id
      return key != null ? String(key) : Math.random().toString()
    })

  const mergedClassName = ['app-table', tableProps.className].filter(Boolean).join(' ')

  return (
    <Table
      {...tableProps}
      columns={columns}
      dataSource={data}
      rowKey={resolvedRowKey}
      className={mergedClassName}
      bordered={tableProps.bordered ?? true}
      size={tableProps.size ?? 'middle'}
      sticky={tableProps.sticky ?? true}
      rowClassName={
        tableProps.rowClassName ??
        ((_record: T, index: number) => (index % 2 === 0 ? 'app-table-row-even' : 'app-table-row-odd'))
      }
      scroll={tableProps.scroll ?? { x: '100%' }}
      style={{ width: '100%', ...(tableProps.style ?? {}) }}
      pagination={{
        ...tableProps.pagination,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        showSizeChanger: true,
        showQuickJumper: true,
        hideOnSinglePage: false,
        pageSizeOptions: ['10', '20', '50', '100'],
        showLessItems: true,
        size: 'default',
        style: {
          background: 'linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)',
          borderRadius: '12px',
          padding: '10px 12px',
          border: '1px solid #e5e7eb',
          marginTop: '12px'
        }
      }}
    />
  );
};

export default DynamicTable;
